import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CheckCircle, Info, ShieldAlert } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { RequestRevokeModal } from "../components/RequestRevokeModal";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import { cn } from "../../../lib/utils";

const ALLOCATION_STATUS_KEYS = {
  Reserved: "status.reserved",
  "Partially Allocated": "inventory.statusPartiallyAllocated",
  Delivered: "inventory.statusDelivered",
  Revoked: "inventory.statusRevoked",
  Allocated: "inventory.statusAllocated",
  Partial: "inventory.statusPartial",
  Shortfall: "inventory.statusShortfall",
};

const MOCK_TODAY = new Date("2026-08-04");

export function AllocationDetailPage() {
  const { t } = useTranslation();
  const { allocationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successToast, setSuccessToast] = useState("");

  // Allocation detail state
  const [allocation, setAllocation] = useState(null);

  // Revocation modal trigger state
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);

  // Load allocation detail
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    inventoryManagementService.getAllocationDetail(allocationId)
      .then((res) => {
        if (!active) return;
        setAllocation(res);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load allocation detail:", err);
        setError(t("validation.allocationNotFound"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [allocationId, t]);

  const isDelivered = useMemo(() => {
    if (!allocation) return false;
    // If status is already Delivered in DB
    if (allocation.header.status === "Delivered") return true;
    // Else check if deliveryDate is in the past relative to today
    const delivery = new Date(allocation.header.deliveryDate);
    return delivery < MOCK_TODAY;
  }, [allocation]);

  // Status text resolver
  const resolvedStatus = useMemo(() => {
    if (!allocation) return "";
    if (allocation.header.status === "Revoked") return "Revoked";
    if (isDelivered) return "Delivered";
    return allocation.header.status; // Reserved or Partially Allocated
  }, [allocation, isDelivered]);

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "—") return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRevokeSubmit = (reason) => {
    setSubmitting(true);
    inventoryManagementService.submitRevokeRequest({
      allocationId: allocation?.header.allocationId,
      reference: allocation?.header.reference,
      reason,
    })
      .then(() => {
        setSuccessToast(t("formActions.revokeSubmittedSuccess"));
        setSubmitting(false);
        setTimeout(() => setSuccessToast(""), 3000);
      })
      .catch((err) => {
        console.error("Failed to submit revoke request:", err);
        alert(t("validation.revokeSubmitError"));
        setSubmitting(false);
      });
  };

  // Status Badge Class resolver
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Reserved":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Partially Allocated":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "Delivered":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Revoked":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        { label: t("inventory.inventoryManagement"), onClick: () => navigate("/inventory-management/stock-position") },
        { label: t("inventory.allocationDetail") },
      ]}
      className="space-y-6 animate-fade-in"
    >
      {/* Toast Alert Success popup */}
      {successToast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in fade-in duration-300 font-sans font-bold text-xs">
          <span>✔️</span>
          <span>{successToast}</span>
        </div>
      )}

      {/* Revocation modal mount */}
      <RequestRevokeModal
        open={revokeModalOpen}
        onOpenChange={setRevokeModalOpen}
        referenceCode={allocation?.header.reference}
        onSubmit={handleRevokeSubmit}
      />

      {/* Custom Title and Back Nav */}
      <div className="space-y-1 font-sans">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs font-bold hover:underline bg-transparent border-0 cursor-pointer focus:outline-none mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t("formActions.backToStockJournal")}</span>
        </button>

        {loading ? (
          <div className="h-10 bg-gray-100 rounded w-1/3 animate-pulse" />
        ) : error ? (
          <h1 className="text-xl font-extrabold text-foreground tracking-tight">
            {t("inventory.allocationDetail")}
          </h1>
        ) : (
          <div className="space-y-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight uppercase">
                {t("inventory.allocationRef", { ref: allocation.header.reference })}
              </h1>
              <span className={cn(
                "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border",
                getStatusBadgeClass(resolvedStatus)
              )}>
                {ALLOCATION_STATUS_KEYS[resolvedStatus] ? t(ALLOCATION_STATUS_KEYS[resolvedStatus]) : resolvedStatus}
              </span>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        /* Loading skeleton */
        <ContentCard bodyClassName="p-6 h-[400px] animate-pulse bg-gray-50/20" />
      ) : error ? (
        /* Empty / Not Found State */
        <div className="p-12 text-center bg-white border border-[var(--color-text-border)] rounded-2xl max-w-md mx-auto shadow-sm font-sans space-y-4">
          <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 grid place-items-center mx-auto">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-foreground">{t("validation.allocationNotFound")}</h4>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1.5 leading-relaxed">
              {t("validation.allocationNotFoundHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/inventory-management/stock-position")}
            className="inline-flex items-center justify-center h-10 px-5 rounded-xl bg-[var(--color-primary)] text-white font-bold text-xs hover:bg-[var(--color-accent)] transition cursor-pointer"
          >
            {t("inventory.returnToStockJournal")}
          </button>
        </div>
      ) : (
        <div className="space-y-6 font-sans">
          {/* Status-specific alert banners */}
          {resolvedStatus === "Revoked" && (
            <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/50 text-rose-800 text-xs font-bold flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 shrink-0" />
              <span>{t("inventory.allocationSubtitleRevoked")}</span>
            </div>
          )}

          {resolvedStatus === "Delivered" && (
            <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 text-emerald-800 text-xs font-bold flex items-center gap-2">
              <CheckCircle className="h-4 w-4 shrink-0" />
              <span>{t("inventory.allocationSubtitleDelivered")}</span>
            </div>
          )}

          {/* Card 1: Allocation Information */}
          <ContentCard title={t("inventory.allocationInformation")} bodyClassName="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-xs font-medium">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("inventory.reference")}
                </span>
                <span className="text-foreground font-mono font-bold">
                  {allocation.header.reference}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("dateTime.date")}
                </span>
                <span className="text-foreground">
                  {formatDate(allocation.header.date)}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("common.customer")}
                </span>
                <span className="text-foreground font-bold">
                  {allocation.header.customerName}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("inventory.iofNumber")}
                </span>
                <span className="text-foreground font-mono">
                  {allocation.header.iofNumber}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("inventory.invoiceNumber")}
                </span>
                <span className="text-foreground font-mono">
                  {allocation.header.invoiceNumber}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("dateTime.invoiceDate")}
                </span>
                <span className="text-foreground">
                  {formatDate(allocation.header.invoiceDate)}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("dateTime.deliveryDate")}
                </span>
                <span className="text-foreground">
                  {formatDate(allocation.header.deliveryDate)}
                </span>
              </div>

              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                  {t("formActions.createdBy")}
                </span>
                <span className="text-foreground">
                  {allocation.header.createdBy}
                </span>
              </div>
            </div>
          </ContentCard>

          {/* Card 2 & 3: Product Allocation Lines with Nested Batches */}
          <div className="space-y-4">
            <h3 className="text-[11px] font-extrabold uppercase tracking-wider text-[var(--color-primary)]">
              {t("inventory.productAllocationLines")}
            </h3>

            {allocation.products.map((product) => (
              <div key={product.id} className="bg-white rounded-2xl border border-[var(--color-text-border)] overflow-hidden shadow-sm">
                {/* Product Header Row */}
                <div className="p-4 sm:p-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2.5">
                      <h4 className="text-sm font-extrabold text-foreground">{product.name}</h4>
                      <span className={cn(
                        "inline-flex px-2 py-0.5 rounded-full text-[9px] font-extrabold border",
                        getStatusBadgeClass(resolvedStatus)
                      )}>
                        {ALLOCATION_STATUS_KEYS[resolvedStatus] ? t(ALLOCATION_STATUS_KEYS[resolvedStatus]) : resolvedStatus}
                      </span>
                    </div>
                    <span className="text-[10px] text-gray-400 font-semibold block mt-0.5 font-mono">
                      {product.partNumber} · {product.manufacturer}
                    </span>
                  </div>

                  {/* Top Right Actions */}
                  {resolvedStatus === "Reserved" ? (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => setRevokeModalOpen(true)}
                      className="h-9 px-4 rounded-xl border border-rose-300 hover:border-rose-500 bg-white text-xs font-bold text-rose-600 hover:bg-rose-50 transition cursor-pointer"
                    >
                      {t("formActions.requestRevoke")}
                    </button>
                  ) : resolvedStatus === "Delivered" ? (
                    <div className="text-[10px] text-gray-400 font-bold bg-gray-50 border px-2 py-1 rounded flex items-center gap-1">
                      <Info className="h-3.5 w-3.5 text-gray-400" />
                      <span>{t("inventory.allocationSubtitleDelivered")}</span>
                    </div>
                  ) : null}
                </div>

                {/* Quantities Row Grid */}
                <div className="p-4 sm:p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-medium border-b border-gray-100 bg-gray-50/20">
                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                      {t("inventory.iofQty")}
                    </span>
                    <span className="text-sm font-extrabold text-foreground font-mono">
                      {product.requestedQty}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                      {t("inventory.allocatedQty")}
                    </span>
                    <span className="text-sm font-extrabold text-foreground font-mono">
                      {product.allocatedQty}
                    </span>
                  </div>

                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                      {t("inventory.statusShortfall")}
                    </span>
                    {product.shortfall > 0 ? (
                      <span className="text-sm font-extrabold text-rose-600 font-mono animate-pulse">
                        {product.shortfall}
                      </span>
                    ) : (
                      <span className="text-sm font-bold text-gray-400 font-mono">—</span>
                    )}
                  </div>

                  <div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 block mb-1">
                      {t("inventory.allocationMethod")}
                    </span>
                    <span className="inline-flex px-2 py-0.5 rounded bg-[var(--color-workspace)]/40 text-[9px] font-extrabold text-[var(--color-primary)] uppercase tracking-wider">
                      {product.allocationMethod === "FEFO" ? t("inventory.fefoLabel") : product.allocationMethod === "FIFO" ? t("inventory.fifoLabel") : product.allocationMethod}
                    </span>
                  </div>
                </div>

                {/* Nested Batches Sub Table */}
                <div className="p-4 sm:p-5">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block mb-3">
                    {t("inventory.allocatedBatchesDetail")}
                  </span>
                  <div className="border border-gray-150 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-[var(--color-workspace)]/30 border-b border-gray-150 font-bold text-[9px] uppercase text-[var(--color-text-secondary)] tracking-wider">
                          <th className="px-4 py-2">{t("inventory.batchNumber")}</th>
                          <th className="px-4 py-2">{t("inventory.warehouse")}</th>
                          <th className="px-4 py-2 text-center">{t("inventory.allocatedQty")}</th>
                          <th className="px-4 py-2 text-right">{t("dateTime.warrantyEndDate2")}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 font-medium">
                        {product.batches.map((batch) => (
                          <tr key={batch.batchNumber} className="hover:bg-[var(--color-workspace)]/5">
                            <td className="px-4 py-2.5 font-mono font-bold text-foreground">
                              {batch.batchNumber}
                            </td>
                            <td className="px-4 py-2.5 text-gray-600">
                              {batch.warehouse}
                            </td>
                            <td className="px-4 py-2.5 text-center font-mono font-bold text-foreground">
                              {batch.allocatedQty}
                            </td>
                            <td className="px-4 py-2.5 text-right text-gray-500 text-[11px]">
                              {formatDate(batch.warrantyEndDate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Card 4: Allocation Summary card */}
          <ContentCard title={t("common.summary")} bodyClassName="p-6 bg-gray-50/20">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block">
                  {t("inventory.products")}
                </span>
                <span className="text-lg font-extrabold text-foreground block mt-1 font-mono">
                  {allocation.summary.productsCount}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 block">
                  {t("formActions.requestedQty")}
                </span>
                <span className="text-lg font-extrabold text-foreground block mt-1 font-mono">
                  {allocation.summary.requestedQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-500 block">
                  {t("inventory.allocatedQty")}
                </span>
                <span className="text-lg font-extrabold text-emerald-600 block mt-1 font-mono">
                  {allocation.summary.allocatedQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-rose-500 block">
                  {t("inventory.shortfallQty")}
                </span>
                <span className={cn("text-lg font-extrabold block mt-1 font-mono", allocation.summary.shortfallQty > 0 ? "text-rose-600 animate-pulse" : "text-gray-400")}>
                  {allocation.summary.shortfallQty}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block">
                  {t("inventory.warehouses")}
                </span>
                <span className="text-lg font-extrabold text-[var(--color-primary)] block mt-1 font-mono">
                  {allocation.summary.numWarehouses}
                </span>
              </div>

              <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm col-span-2 md:col-span-1">
                <span className="text-[9px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block">
                  {t("status.status")}
                </span>
                <span className="text-xs font-bold text-foreground block mt-2 truncate">
                  {ALLOCATION_STATUS_KEYS[resolvedStatus] ? t(ALLOCATION_STATUS_KEYS[resolvedStatus]) : resolvedStatus}
                </span>
              </div>
            </div>
          </ContentCard>
        </div>
      )}
    </AppLayout>
  );
}
